import React from 'react';

// Renders `modalityGroups` (the shape returned by GET /modalities: [{ group, modalities: [{ name, description }] }])
// as collapsible accordion sections of checkbox rows. Open/close state is a controlled prop so callers
// (Settings vs. SessionForm) can each decide their own default-open behavior.
export default function ModalitySelector({ modalityGroups, selected, onToggle, openGroups, onToggleGroup }) {
  return (
    <>
      {modalityGroups.map(group => {
        const isOpen = !!openGroups[group.group];
        const selectedInGroup = group.modalities.filter(m => selected.includes(m.name)).length;
        return (
          <div key={group.group} style={styles.modalityGroup}>
            <button
              type="button"
              style={styles.modalityGroupToggle}
              onClick={() => onToggleGroup(group.group)}
            >
              <span style={styles.modalityGroupLabel}>{group.group}</span>
              <span style={styles.modalityGroupMeta}>
                {selectedInGroup > 0 && <span style={styles.modalityGroupCount}>{selectedInGroup}</span>}
                <span style={styles.chevron}>{isOpen ? '−' : '+'}</span>
              </span>
            </button>
            {isOpen && (
              <div style={styles.modalityList}>
                {group.modalities.map(m => {
                  const active = selected.includes(m.name);
                  return (
                    <div
                      key={m.name}
                      style={{ ...styles.modalityRow, ...(active ? styles.modalityRowActive : {}) }}
                      onClick={() => onToggle(m.name)}
                    >
                      <div style={{ ...styles.modalityCheckbox, ...(active ? styles.modalityCheckboxActive : {}) }}>
                        {active && '✓'}
                      </div>
                      <div>
                        <div style={styles.modalityName}>{m.name}</div>
                        <div style={styles.modalityDesc}>{m.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// Expands any group that contains at least one already-selected modality — used to seed
// `openGroups` on first load so a coach's existing picks are visible without extra clicks.
export function computeInitialOpenGroups(modalityGroups, selected) {
  const initial = {};
  modalityGroups.forEach(group => {
    if (group.modalities.some(m => selected.includes(m.name))) initial[group.group] = true;
  });
  return initial;
}

const styles = {
  modalityGroup: { marginBottom: '10px', borderBottom: '1px solid var(--border-light)' },
  modalityGroupToggle: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 2px', fontFamily: "'DM Sans', sans-serif" },
  modalityGroupLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)' },
  modalityGroupMeta: { display: 'flex', alignItems: 'center', gap: '8px' },
  modalityGroupCount: { background: 'var(--mint)', color: 'var(--sage)', borderRadius: '20px', fontSize: '11px', fontWeight: '600', padding: '1px 8px' },
  chevron: { fontSize: '15px', color: 'var(--text-faint)', width: '14px', textAlign: 'center' },
  modalityList: { display: 'flex', flexDirection: 'column', gap: '8px', paddingBottom: '14px' },
  modalityRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--cream)', cursor: 'pointer' },
  modalityRowActive: { border: '1px solid var(--sage)', background: 'var(--mint)' },
  modalityCheckbox: { width: '18px', height: '18px', borderRadius: '5px', border: '1.5px solid var(--border)', background: 'white', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: 'var(--sage)', fontWeight: '700', marginTop: '1px' },
  modalityCheckboxActive: { border: '1.5px solid var(--sage)', background: 'var(--sage)', color: 'white' },
  modalityName: { fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '3px' },
  modalityDesc: { fontSize: '12px', color: 'var(--text-soft)', lineHeight: 1.5 },
};
